import { create } from 'zustand';
import { conditionalDevtools } from '@/lib/storeMiddleware';
import { bulkBlockContacts, bulkApproveContacts } from '@/api/contacts';
import { trainMessageAsSpam, trainMessageAsGood } from '@/api/trainingApi';
import { executeWithConcurrency } from '@/lib/parallelExecution';
import { useAccountStore } from './accountStore';
import { useEmailStore } from './emailStore';
import { useFolderStore } from './folderStore';
import { useOperationStore } from './operationStore';
import { removeEmailsFromStore } from './emailStateHelpers';
import { resolveEmailContext, type ResolvedEmailContext } from '@/lib/emailContext';
import { mailEvents } from '@/events/mailEvents';

export type BulkAction =
  | 'markRead'
  | 'markUnread'
  | 'delete'
  | 'archive'
  | 'blockSender'
  | 'approveSender'
  | 'trainSpam'
  | 'trainGood';

interface BulkActionStatus {
  action: BulkAction;
  total: number;
  completed: number;
  status: 'idle' | 'running' | 'success' | 'error';
  label?: string;
}

interface BulkOperationsStoreState {
  bulkSelection: Set<string>;
  bulkEditMode: boolean;
  bulkActionStatus: BulkActionStatus | null;
  toggleSelection: (emailId: string) => void;
  selectAllVisible: (emailIds: string[]) => void;
  selectAll: () => void;
  clearSelection: () => void;
  deselectAll: () => void;
  setBulkEditMode: (enabled: boolean) => void;
  applyBulkAction: (action: BulkAction, emailIdsOverride?: string[]) => Promise<void>;
}

const dedupeIds = (ids: string[]): string[] =>
  Array.from(new Set(ids.filter((id): id is string => Boolean(id))));

const reloadActiveMailbox = async () => {
  const accountId = useAccountStore.getState().selectedAccountId;
  const folderId = useFolderStore.getState().selectedFolderId();
  if (!accountId || !folderId) {
    return;
  }
  // Route through the reload event so the bulk outer-catch fallback respects
  // list ownership (sender group / triage) + the skip/defer machinery instead
  // of stamping a mailbox list over a group/label view (L1/E6).
  mailEvents.emit('mail:mailbox:reload', {
    accountId,
    folderId,
    options: { reset: true },
  });
};

export const useBulkOperationsStore = create<BulkOperationsStoreState>()(
  conditionalDevtools(
    (set, get) => ({
      bulkSelection: new Set<string>(),
      bulkEditMode: false,
      bulkActionStatus: null,

    toggleSelection: (emailId) => {
      if (!emailId) {
        return;
      }
      set((state) => {
        const next = new Set(state.bulkSelection);
        if (next.has(emailId)) {
          next.delete(emailId);
        } else {
          next.add(emailId);
        }
        // Unchecking the last box ends multi-select — the mode has nothing
        // left to act on (round 4: no standing toggle to turn it back off).
        return next.size === 0
          ? { bulkSelection: next, bulkEditMode: false }
          : { bulkSelection: next };
      });
    },

    selectAllVisible: (emailIds) => {
      if (!emailIds.length) {
        return;
      }
      set((state) => {
        const next = new Set(state.bulkSelection);
        // Check if all visible emails are already selected
        const allSelected = emailIds.every((id) => state.bulkSelection.has(id));
        
        if (allSelected) {
          // Deselect all visible emails
          emailIds.forEach((id) => {
            if (id) {
              next.delete(id);
            }
          });
        } else {
          // Select all visible emails
          emailIds.forEach((id) => {
            if (id) {
              next.add(id);
            }
          });
        }
        // Toggling everything off ends multi-select (same rule as unchecking
        // the last individual box).
        return next.size === 0
          ? { bulkSelection: next, bulkEditMode: false }
          : { bulkSelection: next };
      });
    },

    selectAll: () => {
      set(() => {
        const ids = useEmailStore.getState().emails
          .map((email) => email.id)
          .filter((id): id is string => Boolean(id));
        return { bulkSelection: new Set(ids) };
      });
    },

    clearSelection: () => {
      set({ bulkSelection: new Set() });
    },

    deselectAll: () => {
      set({ bulkSelection: new Set() });
    },

    setBulkEditMode: (enabled) => {
      set((state) => ({
        bulkEditMode: enabled,
        bulkSelection: enabled ? state.bulkSelection : new Set(),
      }));
    },

    applyBulkAction: async (action, emailIdsOverride) => {
      const selection = emailIdsOverride
        ? dedupeIds(emailIdsOverride)
        : Array.from(get().bulkSelection);
      if (!selection.length) {
        return;
      }

      const contexts = selection
        .map((id) => {
          const context = resolveEmailContext(id);
          if (!context) {
            return null;
          }
          return { id, ...context };
        })
        .filter((entry): entry is { id: string } & ResolvedEmailContext => entry !== null);

      if (!contexts.length) {
        return;
      }

      const operationStore = useOperationStore.getState();
      const trackOperations = !['archive', 'delete', 'trainSpam', 'trainGood', 'approveSender'].includes(action);
      if (trackOperations) {
        contexts.forEach(({ id }) => operationStore.markOperationPending(id));
      }

      try {
        switch (action) {
          case 'markRead':
          case 'markUnread': {
            // The engine owns thread expansion (a row's read = AND of members),
            // optimistic overlay, grouping, and failure restore.
            const { executeEmailAction } = await import('./emailCommands');
            await executeEmailAction(action, {
              kind: 'threadRows',
              ids: contexts.map(({ id }) => id),
            });
            break;
          }
          case 'archive':
          case 'delete': {
            const { executeEmailAction } = await import('./emailCommands');
            const ids = contexts.map(({ id }) => id);
            set((state) => {
              const next = new Set(state.bulkSelection);
              ids.forEach((id) => next.delete(id));
              return { bulkSelection: emailIdsOverride ? state.bulkSelection : next };
            });
            await executeEmailAction(action, { kind: 'threadRows', ids });
            break;
          }
          case 'blockSender':
          case 'approveSender': {
            const isBlock = action === 'blockSender';
            // Group unique contactIds by accountId
            const contactsByAccount = new Map<string, Set<string>>();
            contexts.forEach(({ email, accountId }) => {
              const contactId = email?.contactId ?? null;
              if (contactId) {
                const set = contactsByAccount.get(accountId);
                if (set) {
                  set.add(contactId);
                } else {
                  contactsByAccount.set(accountId, new Set([contactId]));
                }
              }
            });

            // Optimistic: remove emails from store
            const ids = contexts.map(({ id }) => id);
            removeEmailsFromStore(ids);
            set((state) => {
              const next = new Set(state.bulkSelection);
              ids.forEach((id) => next.delete(id));
              return { bulkSelection: emailIdsOverride ? state.bulkSelection : next };
            });

            const accountEntries = Array.from(contactsByAccount.entries());
            const results = await Promise.allSettled(
              accountEntries.map(([accountId, contactIds]) =>
                isBlock
                  ? bulkBlockContacts(accountId, Array.from(contactIds))
                  : bulkApproveContacts(accountId, Array.from(contactIds))
              )
            );
            const failedAccountIds = new Set<string>();
            results.forEach((result, index) => {
              if (result.status === 'rejected') {
                failedAccountIds.add(accountEntries[index][0]);
                console.error(`Bulk ${action} failed`, result.reason);
              }
            });
            if (failedAccountIds.size > 0) {
              // Restore only the failed accounts' emails (clears their grace,
              // re-merges the snapshots, re-projects) and say so.
              const { restoreAfterFailedAction } = await import('./emailCommands');
              const failedEntries = contexts
                .filter(({ accountId }) => failedAccountIds.has(accountId))
                .map(({ id, email }) => ({ id, email }));
              restoreAfterFailedAction(failedEntries, {
                message: `${failedEntries.length} email${failedEntries.length === 1 ? '' : 's'} failed to ${isBlock ? 'block' : 'approve'} — restored`,
              });
            }
            break;
          }
          case 'trainSpam':
          case 'trainGood': {
            const targets = contexts.map(({ id, accountId }) => ({ id, accountId }));
            if (!targets.length) {
              break;
            }
            const label = action === 'trainGood' ? 'ham' : 'spam';
            const trainer =
              action === 'trainGood' ? trainMessageAsGood : trainMessageAsSpam;
            const emailStore = useEmailStore.getState();

            set({
              bulkActionStatus: {
                action,
                label,
                total: targets.length,
                completed: 0,
                status: 'running',
              },
            });

            const results = await executeWithConcurrency(
              targets,
              async ({ id, accountId }) => {
                const training = await trainer({
                  accountId,
                  messageId: id,
                  source: emailIdsOverride ? 'bulk_selection_override' : 'bulk_selection',
                });
                const timestamp = training.trainedAt ?? new Date().toISOString();
                const wasTrained = training.status === 'trained' || training.alreadyTrained;
                if (label === 'ham') {
                  emailStore.updateEmailInPlace(id, {
                    trainedAsGood: wasTrained,
                    lastTrainedLabel: 'ham',
                    lastTrainedAt: timestamp,
                    lastTrainedSource: training.source ?? 'bulk',
                  });
                } else {
                  emailStore.updateEmailInPlace(id, {
                    trainedAsSpam: wasTrained,
                    lastTrainedLabel: 'spam',
                    lastTrainedAt: timestamp,
                    lastTrainedSource: training.source ?? 'bulk',
                  });
                }
                return training;
              },
              {
                concurrency: 3,
                onProgress: (completed, total) => {
                  set((state) => {
                    if (
                      !state.bulkActionStatus
                      || state.bulkActionStatus.action !== action
                    ) {
                      return state;
                    }
                    return {
                      ...state,
                      bulkActionStatus: {
                        ...state.bulkActionStatus,
                        completed,
                        total,
                      },
                    };
                  });
                },
              }
            );

            let trainedCount = 0;
            let skippedCount = 0;
            let failedCount = 0;

            results.forEach(({ result, error }) => {
              if (error || !result) {
                failedCount += 1;
                return;
              }
              if (result.status === 'trained') {
                trainedCount += 1;
              } else {
                skippedCount += 1;
              }
            });

            set((state) => {
              if (
                !state.bulkActionStatus
                || state.bulkActionStatus.action !== action
              ) {
                return state;
              }
              return {
                ...state,
                bulkActionStatus: {
                  ...state.bulkActionStatus,
                  completed: state.bulkActionStatus.total,
                  status: failedCount ? 'error' : 'success',
                },
              };
            });

            const descriptionParts = [
              `${trainedCount} trained`,
              `${skippedCount} skipped`,
            ];
            if (failedCount) {
              descriptionParts.push(`${failedCount} failed`);
            }

            setTimeout(() => {
              set((state) => {
                if (
                  state.bulkActionStatus
                  && state.bulkActionStatus.action === action
                  && state.bulkActionStatus.completed === state.bulkActionStatus.total
                ) {
                return { ...state, bulkActionStatus: null };
              }
              return state;
            });
          }, 2500);
            break;
          }
        default:
          break;
      }

        if (trackOperations) {
          contexts.forEach(({ id }) => operationStore.markOperationComplete(id));
        }
      } catch (error) {
        console.error('Bulk action failed', error);
        if (trackOperations) {
          contexts.forEach(({ id }) => operationStore.markOperationError(id));
        } else {
          await reloadActiveMailbox();
        }
      } finally {
        if (!emailIdsOverride) {
          set({ bulkSelection: new Set() });
        }
      }
    },
  }),
    { name: 'BulkOperationsStore' }
  )
);

// Selection pruning (F10): whenever the visible list changes — refetch reset,
// realtime removal, mailbox/deep-link switch, sender-group entry/exit — drop
// selected ids that are no longer present. The BulkActionBar count and the
// eligibility flags both derive from the pruned set.
let unsubSelectionPrune: (() => void) | null = null;
let selectionPruneDisposed = false;

const startSelectionPrune = () => {
  if (selectionPruneDisposed || unsubSelectionPrune) {
    return;
  }
  unsubSelectionPrune = useEmailStore.subscribe(
    (state) => state.emails,
    (emails) => {
      useBulkOperationsStore.setState((state) => {
        if (state.bulkSelection.size === 0) {
          return {};
        }
        const presentIds = new Set(emails.map((email) => email.id));
        const next = new Set(
          Array.from(state.bulkSelection).filter((id) => presentIds.has(id))
        );
        return next.size === state.bulkSelection.size ? {} : { bulkSelection: next };
      });
    }
  );
};

if (typeof queueMicrotask === 'function') {
  queueMicrotask(startSelectionPrune);
} else {
  setTimeout(startSelectionPrune, 0);
}

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    selectionPruneDisposed = true;
    unsubSelectionPrune?.();
    unsubSelectionPrune = null;
  });
}
